import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useCategories } from "../../../hooks/useCategories";
import { useTailNumbers } from "../../../hooks/useTailNumbers";
import type { Category } from "../../../services/categoryService";

// Common style constants
const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
};

const commonInputStyles = {
  "& .MuiInputLabel-root": {
    color: `${COLORS.white} !important`,
  },
  "& .MuiOutlinedInput-root": {
    color: COLORS.white,
    "& fieldset": {
      borderColor: `${COLORS.white} !important`,
    },
    "&:hover fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
  },
  "& .MuiSvgIcon-root": {
    color: `${COLORS.white} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${COLORS.white} !important`,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: COLORS.white + " !important",
      WebkitTextFillColor: COLORS.white + " !important",
      opacity: 1,
    },
  },
};

type DisabledFieldKeys =
  | "tailNumber"
  | "documentCategory"
  | "serialNumber"
  | "aircraftType"
  | "documentTitle"
  | "description";

interface AircraftInfoFormProps {
  documentScope: "aircraft" | "assembly" | "";
  setDocumentScope: (value: "aircraft" | "assembly" | "") => void;
  assemblyType: string;
  setAssemblyType: (value: string) => void;
  assemblyFolder: string;
  setAssemblyFolder: (value: string) => void;
  assemblyFolders: Category[]; // serial-number folders from useWarehouseCategories
  assemblyCategory: string;
  setAssemblyCategory: (value: string) => void;
  assemblyCategories: Category[];
  tailNumber: string;
  setTailNumber: (value: string) => void;
  documentCategory: string;
  setDocumentCategory: (value: string) => void;
  serialNumber: string;
  setSerialNumber: (value: string) => void;
  aircraftType: string;
  documentTitle: string;
  setDocumentTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  setAircraftType: (value: string) => void;
  disabled?: boolean;
  disabledFields?: Partial<Record<DisabledFieldKeys, boolean>>;
  hideCategoryDropdownWhenTopLevel?: boolean;
  hideScopeSelector?: boolean;
}

export const AircraftInfoForm: React.FC<AircraftInfoFormProps> = ({
  documentScope,
  setDocumentScope,
  assemblyType,
  setAssemblyType,
  assemblyFolder,
  setAssemblyFolder,
  assemblyFolders,
  assemblyCategory,
  setAssemblyCategory,
  assemblyCategories,
  tailNumber,
  setTailNumber,
  documentCategory,
  setDocumentCategory,
  documentTitle,
  setDocumentTitle,
  description,
  setDescription,
  disabled = false,
  disabledFields = {},
  hideCategoryDropdownWhenTopLevel = false,
  hideScopeSelector = false,
}) => {
 
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    getTopLevelCategories,
    getChildrenByParentCode,
    formatLabel,
  } = useCategories(tailNumber);

  const { tailNumbers, loading } = useTailNumbers();

  // State for selected category path (array of codes)
  const [selectedPath, setSelectedPath] = useState<string[]>([]);

  // Determine if documentScope has been selected
  const isScopeSelected = documentScope !== "";

  const getParentCode = useCallback(
    (code: string): string | undefined => {
      return categories.find((cat) => cat.code === code)?.parentCode;
    },
    [categories]
  );

  // Helper function to build category path from root to leaf
  const buildCategoryPath = useCallback((categoryCode: string): string[] => {
    const path: string[] = [categoryCode];

    let current = getParentCode(categoryCode);
    while (current) {
      path.unshift(current);
      current = getParentCode(current);
    }

    return path;
  }, [getParentCode]);

  const isTopLevelCategory = documentCategory
    ? !getParentCode(documentCategory)
    : false;

  // Initialize selectedPath from documentCategory prop
useEffect(() => {
  if (documentCategory) {
    setSelectedPath(buildCategoryPath(documentCategory));
  } else {
    setSelectedPath([]);
  }
}, [documentCategory, buildCategoryPath]);

  const isDisabled = (fieldName: DisabledFieldKeys) =>
    disabled || disabledFields[fieldName] === true;

  const handleDocumentScopeChange = (value: "aircraft" | "assembly" | "") => {
    setDocumentScope(value);
    if (value === "assembly") {
      setTailNumber("");
      setDocumentCategory("");
      setSelectedPath([]);
      setAssemblyType("");
      setAssemblyFolder("");
      setAssemblyCategory("");
    } else if (value === "aircraft") {
      setAssemblyType("");
      setAssemblyFolder("");
      setAssemblyCategory("");
    } else {
      // Reset all when empty
      setTailNumber("");
      setDocumentCategory("");
      setAssemblyType("");
      setAssemblyFolder("");
      setAssemblyCategory("");
      setSelectedPath([]);
    }
  };

  const handleAssemblyTypeChange = (value: string) => {
    setAssemblyType(value);
    setAssemblyFolder("");
    setAssemblyCategory("");
  };

  // Handle selection change for a specific level
const handleSelectionChange = (level: number, code: string) => {
  if (!code) return;

  // rebuild path instead of slicing
  const newPath = buildCategoryPath(code);

  setSelectedPath(newPath);
  setDocumentCategory(code);
};
  
  const getOptionsForLevel = (level: number) => {
  if (level === 0) {
    const topLevel = getTopLevelCategories().map((cat) => ({
        code: cat.code,
        label: formatLabel(cat),
        requiresSerialNumber: cat.requiresSerialNumber,
        description: cat.description,
      }));
    return topLevel;
  }

  const parentCode = selectedPath[level - 1];

  const children = getChildrenByParentCode(parentCode);

  return children.map((cat) => ({
    code: cat.code,
    label: formatLabel(cat),
    requiresSerialNumber: cat.requiresSerialNumber,
    description: cat.description,
  }));
};


  return (
    <Box
      sx={{
        height: "80vh",
        p: 1,
        border: "1px solid white",
        borderRadius: "8px",
        background: "none",
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ color: "hsl(19 101% 51% / 1)" }}
      >
        {documentScope === "aircraft" ? "Aircraft Information" : "Assembly Information"}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {!hideScopeSelector && (
          <TextField
            select
            fullWidth
            size="small"
            label="Upload Type"
            value={documentScope}
            onChange={(e) =>
              handleDocumentScopeChange(
                e.target.value as "aircraft" | "assembly" | ""
              )
            }
            sx={commonInputStyles}
          >
            <MenuItem value="" disabled>
              Select Upload Type
            </MenuItem>
            <MenuItem value="aircraft">Aircraft</MenuItem>
            <MenuItem value="assembly">OffWing  Assemblies</MenuItem>
          </TextField>
        )}

        {isScopeSelected && (
          <>
            {documentScope === "aircraft" && (
              <>
        <Autocomplete
          size="small"
          options={tailNumbers}
          loading={loading}
          value={tailNumber || null}
          onChange={(event, newValue) => {
            const nextTailNumber = newValue || "";
            setTailNumber(nextTailNumber);
            setDocumentCategory("");
            setSelectedPath([]);
          }}
          disabled={isDisabled("tailNumber")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aircraft Registration No."
              placeholder="e.g., N12345"
              required
              helperText="Required field"
              sx={{
                ...commonInputStyles,
                "& .MuiAutocomplete-popupIndicator": {
                  color: `${COLORS.white} !important`,
                },
                "& .MuiAutocomplete-clearIndicator": {
                  color: `${COLORS.white} !important`,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{
            "& .MuiOutlinedInput-root": {
              ...commonInputStyles["& .MuiOutlinedInput-root"],
            },
          }}
        />
        {/* Dynamic cascading category dropdowns */}
        {(!hideCategoryDropdownWhenTopLevel || !isTopLevelCategory) &&
          Array.from({ length: selectedPath.length + 1 }, (_, level) => {
          const options = getOptionsForLevel(level);
          // Keep the top-level category dropdown visible even when options are empty/loading.
          if (level > 0 && options.length === 0) return null;
          
          const selectedValue =
            options.find((opt) => opt.code === selectedPath[level]) || null;
          
          return (
            <Autocomplete
              key={level}
              size="small"
              options={options}
              value={selectedValue}
              onChange={(event, newValue) => {
                handleSelectionChange(level, newValue?.code || "");
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              disabled={disabled || categoriesLoading}
              loading={categoriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    level === 0
                      ? "Document Category"
                      : `Subcategory Level ${level}`
                  }
                  required={level === 0}
                  helperText={
                    level === 0 ? categoriesError || "Required field" : ""
                  }
                  error={level === 0 && !!categoriesError}
                  fullWidth
                  sx={{
                    ...commonInputStyles,
                    "& .MuiAutocomplete-popupIndicator": {
                      color: `${COLORS.white} !important`,
                    },
                    "& .MuiAutocomplete-clearIndicator": {
                      color: `${COLORS.white} !important`,
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {categoriesLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                  </Box>
                </li>
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  ...commonInputStyles["& .MuiOutlinedInput-root"],
                },
              }}
            />
          );
        })}
        
        {/* Serial Number field - commented out */}
        {/* Aircraft Type field - commented out */}
        
        <TextField
          fullWidth
          size="small"
          label="Form No."
          value={documentTitle}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentTitle(e.target.value.toUpperCase())}
          placeholder="e.g., Maintenance Manual"
          required
          disabled={isDisabled("documentTitle")}
          helperText="Required field"
          sx={commonInputStyles}
        />

        <TextField
          fullWidth
          size="small"
          label="Document Description"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
          placeholder="Brief description of the document"
          required
          disabled={isDisabled("description")}
          multiline
          rows={1}
          sx={commonInputStyles}
        />
        </>
        )}

        {documentScope === "assembly" && (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label="Assembly Type"
              value={assemblyType}
              onChange={(e) => handleAssemblyTypeChange(e.target.value)}
              sx={commonInputStyles}
              required
            >
              <MenuItem value="" disabled>Select Assembly Type</MenuItem>
              <MenuItem value="engine">Engine</MenuItem>
              <MenuItem value="apu">APU</MenuItem>
              <MenuItem value="landingGear">Landing Gear</MenuItem>
            </TextField>

            {assemblyType && (
              <TextField
                select
                fullWidth
                size="small"
                label="Assembly"
                value={assemblyFolder}
                onChange={(e) => setAssemblyFolder(e.target.value)}
                sx={commonInputStyles}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 320 },
                    },
                  },
                }}
                required
              >
                <MenuItem value="" disabled>Select Assembly</MenuItem>
                {assemblyFolders.map((folder) => (
                  <MenuItem key={folder.code} value={folder.code}>
                    {folder.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {assemblyFolder && assemblyCategories.length > 0 && (
              <TextField
                select
                fullWidth
                size="small"
                label="Document Category"
                value={assemblyCategory}
                onChange={(e) => setAssemblyCategory(e.target.value)}
                sx={commonInputStyles}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 320 },
                    },
                  },
                }}
                required
              >
                <MenuItem value="" disabled>Select Category</MenuItem>
                {assemblyCategories.map((cat) => (
                  <MenuItem
                    key={cat.code}
                    value={cat.code}
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      py: 1,
                    }}
                  >
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              size="small"
              label="Form No."
              value={documentTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDocumentTitle(e.target.value.toUpperCase())
              }
              placeholder="e.g., Maintenance Manual"
              required
              disabled={isDisabled("documentTitle")}
              helperText="Required field"
              sx={commonInputStyles}
            />
          </>
        )}
          </>
        )}
      </Box>
    </Box>
  );
};
